import { Table, CombinedTable, Reservation } from "@/types/table";
import { FloorPlanZone } from "@/hooks/useFloorPlanZones";
import { AddFixtureDialog } from "../AddFixtureDialog";
import { ZoneManagementDialog } from "../ZoneManagementDialog";
import { ZoneLayoutEditor } from "../ZoneLayoutEditor";
import { TableSwapConfirmDialog } from "../TableSwapConfirmDialog";
import { CombinedTableSelectionDialog, CombinedTableOption } from "../CombinedTableSelectionDialog";
import { FloorPlanSetupRequestDialog } from "../FloorPlanSetupRequestDialog";
import { FixtureType } from "@/types/fixture";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

interface SwapReservationInfo {
  id: string;
  customerName: string;
  numberOfPeople: number;
  tableName: string;
  tableId: string | null;
  combinedTableId: string | null;
}

interface CapacityWarningInfo {
  reservationId: string;
  tableName: string;
  tableCapacity: number;
  numberOfPeople: number;
  tableId: string | null;
  combinedTableId: string | null;
}

interface FloorPlanDialogsProps {
  // Add fixture
  showAddFixtureDialog: boolean;
  onShowAddFixtureDialogChange: (open: boolean) => void;
  onAddFixture: (type: FixtureType, name: string | null, color: string) => void;

  // Exit confirm
  showExitConfirmDialog: boolean;
  onShowExitConfirmDialogChange: (open: boolean) => void;
  onDiscardChanges: () => void;
  onSaveAndExit: () => void;

  // Reset confirm
  showResetConfirmDialog: boolean;
  onShowResetConfirmDialogChange: (open: boolean) => void;
  onResetConfirm: () => void;

  // Zone management
  zoneDialogOpen: boolean;
  onZoneDialogOpenChange: (open: boolean) => void;
  editingZone: FloorPlanZone | null;
  onZoneSave: (zoneData: Partial<FloorPlanZone>) => void;
  isZoneLoading: boolean;

  // Swap
  showSwapDialog: boolean;
  onShowSwapDialogChange: (open: boolean) => void;
  swapDraggedReservation: SwapReservationInfo | null;
  swapTargetReservation: SwapReservationInfo | null;
  onSwapConfirm: () => void;

  // Combined table selection
  showCombinedSelectionDialog: boolean;
  onShowCombinedSelectionDialogChange: (open: boolean) => void;
  pendingCombinedDragReservation: Reservation | null;
  combinedSelectionOptions: CombinedTableOption[];
  onCombinedTableSelection: (combinedTableId: string, swapWithReservationId?: string) => void;
  onSingleTableFromDialog: (tableId: string) => void;
  pendingCombinedTargetTableId?: string;
  pendingCombinedTargetTableName?: string;

  // Capacity warning
  showCapacityWarningDialog: boolean;
  onShowCapacityWarningDialogChange: (open: boolean) => void;
  capacityWarningInfo: CapacityWarningInfo | null;
  onCapacityWarningConfirm: () => void;
  onCapacityWarningCancel: () => void;

  // Zone layout editor
  showLayoutEditor: boolean;
  onShowLayoutEditorChange: (open: boolean) => void;
  zones: FloorPlanZone[];
  onUpdateZonePosition: (zoneId: string, layoutRow: number, layoutCol: number) => void;

  // Floor plan setup request
  showFloorPlanRequest: boolean;
  onShowFloorPlanRequestChange: (open: boolean) => void;
  tables: Table[];
}

export const FloorPlanDialogs = ({
  showAddFixtureDialog,
  onShowAddFixtureDialogChange,
  onAddFixture,
  showExitConfirmDialog,
  onShowExitConfirmDialogChange,
  onDiscardChanges,
  onSaveAndExit,
  showResetConfirmDialog,
  onShowResetConfirmDialogChange,
  onResetConfirm,
  zoneDialogOpen,
  onZoneDialogOpenChange,
  editingZone,
  onZoneSave,
  isZoneLoading,
  showSwapDialog,
  onShowSwapDialogChange,
  swapDraggedReservation,
  swapTargetReservation,
  onSwapConfirm,
  showCombinedSelectionDialog,
  onShowCombinedSelectionDialogChange,
  pendingCombinedDragReservation,
  combinedSelectionOptions,
  onCombinedTableSelection,
  onSingleTableFromDialog,
  pendingCombinedTargetTableId,
  pendingCombinedTargetTableName,
  showCapacityWarningDialog,
  onShowCapacityWarningDialogChange,
  capacityWarningInfo,
  onCapacityWarningConfirm,
  onCapacityWarningCancel,
  showLayoutEditor,
  onShowLayoutEditorChange,
  zones,
  onUpdateZonePosition,
  showFloorPlanRequest,
  onShowFloorPlanRequestChange,
  tables,
}: FloorPlanDialogsProps) => {
  return (
    <>
      <AddFixtureDialog
        open={showAddFixtureDialog}
        onOpenChange={onShowAddFixtureDialogChange}
        onAdd={onAddFixture}
      />

      <AlertDialog open={showExitConfirmDialog} onOpenChange={onShowExitConfirmDialogChange}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Modifications non enregistrées</AlertDialogTitle>
            <AlertDialogDescription>
              Vous avez des modifications non enregistrées. Que souhaitez-vous faire ?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-col sm:flex-row gap-2">
            <AlertDialogCancel onClick={() => onShowExitConfirmDialogChange(false)}>
              Annuler
            </AlertDialogCancel>
            <Button variant="outline" onClick={onDiscardChanges}>
              Quitter sans enregistrer
            </Button>
            <AlertDialogAction onClick={onSaveAndExit}>
              Enregistrer et quitter
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={showResetConfirmDialog} onOpenChange={onShowResetConfirmDialogChange}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Réinitialiser le plan</AlertDialogTitle>
            <AlertDialogDescription>
              Cette action va réinitialiser la disposition de toutes les tables à leur position par défaut. Voulez-vous continuer ?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={onResetConfirm}>
              Réinitialiser
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <ZoneManagementDialog
        open={zoneDialogOpen}
        onOpenChange={onZoneDialogOpenChange}
        zone={editingZone}
        onSave={onZoneSave}
        isLoading={isZoneLoading}
      />

      <TableSwapConfirmDialog
        open={showSwapDialog}
        onOpenChange={onShowSwapDialogChange}
        draggedReservation={swapDraggedReservation}
        targetReservation={swapTargetReservation}
        onConfirm={onSwapConfirm}
      />

      <CombinedTableSelectionDialog
        open={showCombinedSelectionDialog}
        onOpenChange={(open) => {
          onShowCombinedSelectionDialogChange(open);
        }}
        draggedReservation={pendingCombinedDragReservation}
        options={combinedSelectionOptions}
        onSelect={onCombinedTableSelection}
        onSelectSingleTable={onSingleTableFromDialog}
        targetTableId={pendingCombinedTargetTableId}
        targetTableName={pendingCombinedTargetTableName}
      />

      <AlertDialog open={showCapacityWarningDialog} onOpenChange={onShowCapacityWarningDialogChange}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Capacité insuffisante</AlertDialogTitle>
            <AlertDialogDescription>
              {capacityWarningInfo && (
                <>
                  La table <strong>{capacityWarningInfo.tableName}</strong> a une capacité de{" "}
                  <strong>{capacityWarningInfo.tableCapacity} places</strong>, mais cette réservation
                  compte <strong>{capacityWarningInfo.numberOfPeople} personnes</strong>.
                  <br /><br />
                  Voulez-vous tout de même déplacer cette réservation ?
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={onCapacityWarningCancel}>
              Annuler
            </AlertDialogCancel>
            <AlertDialogAction onClick={onCapacityWarningConfirm}>
              Confirmer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={showLayoutEditor} onOpenChange={onShowLayoutEditorChange}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Disposition des zones</DialogTitle>
          </DialogHeader>
          <ZoneLayoutEditor
            zones={zones}
            onUpdateZonePosition={onUpdateZonePosition}
            onClose={() => onShowLayoutEditorChange(false)}
          />
        </DialogContent>
      </Dialog>

      <FloorPlanSetupRequestDialog
        open={showFloorPlanRequest}
        onOpenChange={onShowFloorPlanRequestChange}
        tables={tables}
        onRefetchTables={() => {}}
      />
    </>
  );
};
